const { ArgumentNullException } = require('exceptions');
const { isUfeEnvLocal, isUfeEnvQA } = require('utils/Env');
const { wrapHOCComponent } = require('utils/framework/wrapHOCComponent');
const Empty = require('constants/empty');
const ErrorBorder = require('utils/framework/hocs/withErrorBoundary/ErrorBorder');
const logError = require('utils/framework/logError');
const React = require('react');

const LocalOrQAEnvironment = isUfeEnvQA || isUfeEnvLocal;

/**
 * @description Creates new JSX element using provided render function.
 * It invokes original render function within the try/catch block.
 * In case of exception `null` will be returned.
 *
 * @param {Function} render Provider is responsible for rendering original component and returning it JSX representation.
 *
 * @param {String} displayName Component's displayName attribute. It's used for logging purposes only.
 *
 * @return {JSX.Element} Shallow copy of original component's JSX element or `null` in case there was an exception.
 */
const createElement = (render, displayName) => {
    try {
        const element = render();

        if (element) {
            const newElement = {
                ...element,
                props: { ...element.props }
            };

            return newElement;
        } else {
            return null;
        }
    } catch (error) {
        let source;

        try {
            source = displayName.split('(')[0];
        } catch {
            source = 'withErrorBoundary';
        }

        logError(error, { source });

        return null;
    }
};

/**
 * @description The function replaces component's render function with new implementation.
 * We do it only for SSR because react error boundary works only client side.
 *
 * @param {React.Component} component Component which render method should be proxied.
 *
 * @return {React.Component} Component received via function argument but with proxied render behaviour.
 */
const proxyRenderFunction = component => {
    let componentToRender = component;
    const { displayName, prototype, type } = component;
    const isFunctionalHOC = !prototype?.render;
    // Flag to indicate that component was wrapped with HOC created by `connect` function.
    const isConnectHOC = !!type?.name;

    if (isFunctionalHOC) {
        const componentToProxy = isConnectHOC ? type : component;
        componentToProxy.isFunctional = true;
        const componentName = componentToProxy.componentName;

        const ProxyComponent = props => {
            return createElement(() => componentToProxy(props), displayName);
        };

        componentToRender = ProxyComponent;
        componentToRender.displayName = displayName;
        componentToRender.componentName = componentName;
        componentToRender.WrappedComponent = component.WrappedComponent;
        componentToRender.isFunctional = true;
    } else {
        const originalRender = prototype.render;

        prototype.render = function () {
            return createElement(() => originalRender.call(this), displayName);
        };
    }

    return componentToRender;
};

/**
 * @description Searches `ErrorBoundary`component in a chain of HOCs, applied to it.
 * It returns `null` when `ErrorBoundary`component is not found.
 *
 * @param {React.Component} wrappedComponent Component wrapped with HOCs to search in.
 *
 * @return {React.Component} `null` or wrapped with `withErrorBoundary` HOC component.
 */
const findErrorBoundaryComponent = wrappedComponent => {
    let component = wrappedComponent;

    while (component) {
        if (component.errorBoundary) {
            return component;
        } else {
            component = component.WrappedComponent;
        }
    }

    return null;
};

/**
 * @description Rebuild `WrappedComponent` to omit previous `withErrorBoundary` HOC.
 *
 * @param {React.Component} wrappedComponent Wrapped with another HOC(s) component.
 *
 * @param {React.Component} errorBoundaryComponent `ErrorBoundary` component.
 *
 * @param {Function} withUserHOC User HOC which goes after `withErrorBoundary` HOC.
 *
 * @return {React.Component} Original or wrapped component.
 */
const reCreateWrappedComponent = (componentToReWrap, withUserHOC, hocArguments = Empty.Array) => {
    let newWrappedComponent = componentToReWrap;

    if (LocalOrQAEnvironment) {
        // Because in local or QA environments we measure components and HOCs render time
        // we need to wrap WrappedComponent again with `withRenderTimeMeasurements` HOC.
        const { withRenderTimeMeasurements } = require('utils/framework/hocs/withRenderTimeMeasurements');
        newWrappedComponent = withRenderTimeMeasurements(newWrappedComponent);
    }

    // Decorate component with user defined HOC. In addition, we pass HOC optional arguments, if they were used originally.
    newWrappedComponent = withUserHOC(newWrappedComponent, ...hocArguments);

    if (Sephora.isNodeRender) {
        newWrappedComponent = proxyRenderFunction(newWrappedComponent);
    }

    return newWrappedComponent;
};

/**
 * @description Creates `withErrorBoundary` HOC.
 *
 * @param {Function} withUserHOC User's HOC to wrap with `withErrorBoundary` HOC.
 *
 * @return {Function} New `withErrorBoundary` HOC instance.
 */
const createHOC = withUserHOC => {
    if (withUserHOC == null) {
        throw new ArgumentNullException('withUserHOC');
    }

    /**
     * @description The HOC wraps incoming component with higher-order component `ErrorBorder`.
     * It creates an error boundary for the component and all applied to it HOCs.
     * Only very first HOC (in a chain of HOCs applied to component) can be a `withErrorBoundary` HOC.
     * In case it was already applied we delete it by re-wrapping component with only user defined HOCs.
     *
     * @param {React.Component} WrappedComponent Target component to wrap. It can be a component already wrapped with HOC.
     *
     * @return {React.Component} Сomponent wrapped with ErrorBoundary.
     */
    function withErrorBoundary(WrappedComponent) {
        let ComponentToRender = WrappedComponent;

        if (Sephora.isNodeRender) {
            ComponentToRender = proxyRenderFunction(ComponentToRender);
        }

        const errorBoundaryComponent = findErrorBoundaryComponent(ComponentToRender);

        if (errorBoundaryComponent) {
            // `componentToReWrap` is what was `WrappedComponent` within `withErrorBoundary` HOC.
            const componentToReWrap = errorBoundaryComponent.WrappedComponent;
            const { hocArguments } = ComponentToRender;
            ComponentToRender = reCreateWrappedComponent(componentToReWrap, withUserHOC, hocArguments);
        }

        const ErrorBoundary = props => {
            let newProps = props;

            if (LocalOrQAEnvironment) {
                const { useRenderTimeMeasurements } = require('utils/framework/hooks/useRenderTimeMeasurements');
                const stackId = Symbol();
                useRenderTimeMeasurements(ComponentToRender, stackId);
                newProps = {
                    ...props,
                    stackId
                };
            }

            if (Sephora.isNodeRender) {
                return <ComponentToRender {...newProps} />;
            } else {
                return (
                    <ErrorBorder>
                        <ComponentToRender {...newProps} />
                    </ErrorBorder>
                );
            }
        };

        ErrorBoundary.errorBoundary = true;

        return wrapHOCComponent(ErrorBoundary, 'ErrorBoundary', [ComponentToRender]);
    }

    return withErrorBoundary;
};

module.exports = { createHOC };
